import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { defineMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { catchAll } from '@sorare/routing';

import {
  ManagerProgressionTaskState,
  Sport,
  StoryState,
} from '__generated__/globalTypes';
import { Dropdown } from 'atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from 'atoms/icons';
import { Binoculars } from 'atoms/icons/Binoculars';
import { HomeIcon } from 'atoms/icons/HomeIcon';
import { Market } from 'atoms/icons/Market';
import { More } from 'atoms/icons/More';
import { MyCards } from 'atoms/icons/MyCards';
import { PlayFootball } from 'atoms/icons/PlayFootball';
import { PlayMLB } from 'atoms/icons/PlayMLB';
import { PlayNBA } from 'atoms/icons/PlayNBA';
import { Horizontal } from 'atoms/layout/flex';
import { ManagerTaskTooltip } from 'components/onboarding/managerTask/ManagerTaskTooltip';
import { MarketplaceOnboardingTask } from 'components/onboarding/managerTask/MarketplaceOnboardingTask';
import {
  ANY_SPORT_MARKET,
  ANY_SPORT_MY_CARDS,
  ANY_SPORT_PLAY,
  ANY_SPORT_SCOUTING_CENTER,
  FOOTBALL_PLAY,
  MLB_HOME,
  NBA_HOME,
} from 'constants/__generated__/routes';
import { useCurrentUserContext } from 'contexts/currentUser';
import {
  MarketplaceOnboardingStep,
  useManagerTaskContext,
} from 'contexts/managerTask';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useCountHighlightedMissions } from 'hooks/missions/useCountHighlightedMissions';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useEvents } from 'lib/events/useEvents';
import { navLabels, scoutingCenter } from 'lib/glossary';
import {
  generateSportPath,
  matchAnySportPath,
} from 'lib/routing/generateSportPath';
import { laptopAndAbove } from 'style/mediaQuery';

import { NewNavItem } from '../../NewNavItem';
import { MoreNavItems } from './MoreNavItems';
import { SearchNavItem } from './SearchNavItem';
import { useNavData } from './useNavData';

const Root = styled(Horizontal)`
  width: 100%;
  position: relative;

  & > * {
    flex: 1;
  }
  @media ${laptopAndAbove} {
    flex-direction: column;
    align-items: stretch;
  }
`;
const DropdownContent = styled(Horizontal)`
  background-color: var(--c-black);
  padding: var(--half-unit) var(--half-unit) var(--intermediate-unit)
    var(--half-unit);
`;

const PAGES = {
  home: {
    [Sport.BASEBALL]: MLB_HOME,
    [Sport.FOOTBALL]: FOOTBALL_PLAY,
    [Sport.NBA]: NBA_HOME,
  },
} as const;

const ICONS = {
  [Sport.BASEBALL]: PlayMLB,
  [Sport.FOOTBALL]: PlayFootball,
  [Sport.NBA]: PlayNBA,
} as const;

const itemsToClaim = defineMessage({
  id: 'NavItems.playItemsToClaim',
  defaultMessage: '{count, plural, one {# item} other {# items}} to claim',
});

type Props = { sport: Sport; sidebarCollapsed?: boolean };
export const NewNavItems = ({ sport, sidebarCollapsed }: Props) => {
  const {
    flags: { useNbaPostseasonRevamp = false },
  } = useFeatureFlags();
  const { formatMessage } = useIntl();
  const track = useEvents();
  const location = useLocation();
  const navigate = useNavigate();
  const isLaptopAndAbove = useIsLaptop();
  const { task, setStep } = useManagerTaskContext();
  const navItemsVariant = isLaptopAndAbove ? 'horizontal' : 'vertical';
  const { currentUser } = useCurrentUserContext();

  const PlayIcon = ICONS[sport];

  const { football, mlb, nba } = useNavData(sport);

  const showLiveDot = {
    [Sport.FOOTBALL]: !!football?.football?.currentManagerHasLiveGames,
    [Sport.BASEBALL]: !!mlb?.so5?.so5Fixture?.mySo5LineupsCount,
    [Sport.NBA]: !!nba?.so5?.so5Fixture?.mySo5LineupsCount,
  }[sport];

  const data = {
    ...(football || {}),
    ...(mlb || {}),
    ...(nba || {}),
  };

  const nbBoxes =
    data.currentUser?.unopenedProbabilisticBundles?.totalCount || 0;

  const unclaimedRewardsCount =
    data.currentUser?.unclaimedSo5Rewards?.length || 0;

  const tasksHighlightedCount = useCountHighlightedMissions({
    currentUser: football?.currentUser,
  });

  const playBadgeCount = {
    [Sport.FOOTBALL]: [
      football?.so5?.myAvailableFixtureStory?.aasmState === StoryState.READY,
      tasksHighlightedCount,
    ].filter(Boolean).length,
    [Sport.BASEBALL]: 0,
    [Sport.NBA]: 0,
  }[sport];

  const unclaimedMarketTasksCount = currentUser?.footballUserProfile
    ?.commonGameOnboarded
    ? football?.market?.tasks?.filter(
        t => t.aasmState === ManagerProgressionTaskState.COMPLETED
      )?.length || 0
    : 0;

  const marketBadgeCount = {
    [Sport.FOOTBALL]: unclaimedMarketTasksCount,
    [Sport.BASEBALL]: 0,
    [Sport.NBA]: 0,
  }[sport];

  const myCardsRewardsCount = nbBoxes + unclaimedRewardsCount;
  const shouldShowNBAHome = useNbaPostseasonRevamp && sport === Sport.NBA;

  return (
    <Root>
      {isLaptopAndAbove && sidebarCollapsed && (
        <SearchNavItem
          variant={navItemsVariant}
          icon={() => <FontAwesomeIcon icon={faSearch} size="sm" />}
          label={formatMessage(navLabels.search)}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}
      {(sport === Sport.BASEBALL || shouldShowNBAHome) && (
        <NewNavItem
          variant={navItemsVariant}
          icon={() => <HomeIcon size={isLaptopAndAbove ? 2 : 2.5} />}
          label={formatMessage(navLabels.home)}
          to={PAGES.home[sport]}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}
      <NewNavItem
        variant={navItemsVariant}
        icon={({ isActive }) => (
          <PlayIcon size={isLaptopAndAbove ? 2 : 2.5} active={isActive} />
        )}
        label={formatMessage(navLabels.play)}
        to={generateSportPath(ANY_SPORT_PLAY, {
          sport,
        })}
        showLiveDot={sport !== Sport.NBA && showLiveDot}
        badge={playBadgeCount > 0 ? { content: playBadgeCount } : undefined}
        sidebarCollapsed={sidebarCollapsed}
      />
      <NewNavItem
        variant={navItemsVariant}
        icon={() => <Binoculars size={isLaptopAndAbove ? 2 : 2.5} />}
        label={formatMessage(scoutingCenter.scout)}
        to={generateSportPath(ANY_SPORT_SCOUTING_CENTER, {
          sport,
        })}
        onClick={() =>
          track('Click Scout Nav Item', {
            sport,
          })
        }
        sidebarCollapsed={sidebarCollapsed}
      />
      <ManagerTaskTooltip
        TaskComponent={MarketplaceOnboardingTask}
        onClick={() => {
          navigate(
            generateSportPath(ANY_SPORT_MARKET, {
              sport,
            })
          );
          setStep(MarketplaceOnboardingStep.managerSalesLink);
        }}
        disable={!task}
        name={MarketplaceOnboardingStep.menu}
      >
        <NewNavItem
          variant={navItemsVariant}
          icon={({ isActive }) => (
            <Market size={isLaptopAndAbove ? 2 : 2.5} active={isActive} />
          )}
          label={formatMessage(navLabels.transferMarket)}
          to={generateSportPath(ANY_SPORT_MARKET, {
            sport,
          })}
          sidebarCollapsed={sidebarCollapsed}
          badge={{
            content: marketBadgeCount,
          }}
        />
      </ManagerTaskTooltip>
      <NewNavItem
        variant={navItemsVariant}
        icon={({ isActive }) => (
          <MyCards size={isLaptopAndAbove ? 2 : 2.5} active={isActive} />
        )}
        label={formatMessage(navLabels.myCards)}
        to={generateSportPath(ANY_SPORT_MY_CARDS, {
          sport,
        })}
        badge={{
          content: myCardsRewardsCount,
          ariaLabel: formatMessage(itemsToClaim, {
            count: myCardsRewardsCount,
          }),
        }}
        keepSearchParams={
          !!matchAnySportPath(catchAll(ANY_SPORT_MY_CARDS), location.pathname)
        }
        sidebarCollapsed={sidebarCollapsed}
      />
      {isLaptopAndAbove && (
        <Dropdown
          forceDesktopBehavior
          side="right"
          gap={24}
          label={({ onClick }) => (
            <button onClick={onClick} type="button">
              <NewNavItem
                icon={() => <More size={2} />}
                label={formatMessage(navLabels.more)}
                sidebarCollapsed={sidebarCollapsed}
              />
            </button>
          )}
        >
          {({ closeDropdown }) => (
            <DropdownContent>
              <MoreNavItems onClick={closeDropdown} />
            </DropdownContent>
          )}
        </Dropdown>
      )}
    </Root>
  );
};
